"use strict";
import { TimePeriod, toHistoryDuration } from "appGraphql/data/util";
import { refitChartContentAtom } from "components/Charts/ChartModel";
import { ChartSkeleton } from "components/Charts/LoadingState";
import { PriceChart } from "components/Charts/PriceChart";
import { LineChart } from "components/Charts/StackedLineChart";
import { VolumeChart } from "components/Charts/VolumeChart";
import { ChartType, PriceChartType } from "components/Charts/utils";
import { AdvancedPriceChartToggle } from "components/Tokens/TokenDetails/ChartSection/AdvancedPriceChartToggle";
import { ChartTypeDropdown } from "components/Tokens/TokenDetails/ChartSection/ChartTypeSelector";
import {
  useTDPPriceChartData,
  useTDPTVLChartData,
  useTDPVolumeChartData
} from "components/Tokens/TokenDetails/ChartSection/hooks";
import { DataQuality } from "components/Tokens/TokenDetails/ChartSection/util";
import {
  DISPLAYS,
  ORDERED_TIMES,
  getTimePeriodFromDisplay
} from "components/Tokens/TokenTable/VolumeTimeFrameSelector";
import { useAtomValue } from "jotai/utils";
import { useTDPContext } from "pages/TokenDetails/TDPContext";
import { useMemo, useState } from "react";
import { Trans } from "react-i18next";
import { ThemeProvider } from "theme";
import { Flex, SegmentedControl, styled, useMedia } from "ui/src";
export const TDP_CHART_HEIGHT_PX = 450;
const TDP_CHART_SELECTOR_OPTIONS = [ChartType.PRICE, ChartType.VOLUME, ChartType.TVL];
export const DEFAULT_PILL_TIME_SELECTOR_OPTIONS = ORDERED_TIMES.map((time) => ({
  value: DISPLAYS[time]
}));
export const ChartActionsContainer = styled(Flex, {
  flexDirection: "row-reverse",
  width: "100%",
  justifyContent: "space-between",
  alignItems: "center",
  mt: 12,
  $md: {
    flexDirection: "column",
    gap: 16
  }
});
const InvalidChartMessage = () => <Trans i18nKey="chart.error.tokens" />;
export function useCreateTDPChartState(tokenDBAddress, currencyChainName) {
  const [timePeriod, setTimePeriod] = useState(TimePeriod.DAY);
  const [chartType, setChartType] = useState(ChartType.PRICE);
  const [priceChartType, setPriceChartType] = useState(PriceChartType.LINE);
  const variables = { address: tokenDBAddress, chain: currencyChainName, duration: toHistoryDuration(timePeriod) };
  const priceQuery = useTDPPriceChartData({ variables, skip: chartType !== ChartType.PRICE, priceChartType });
  const volumeQuery = useTDPVolumeChartData(variables, chartType !== ChartType.VOLUME);
  const tvlQuery = useTDPTVLChartData(variables, chartType !== ChartType.TVL);
  return useMemo(() => {
    const { disableCandlestickUI } = priceQuery;
    const activeQuery = (() => {
      switch (chartType) {
        case ChartType.PRICE:
          return priceQuery;
        case ChartType.VOLUME:
          return volumeQuery;
        case ChartType.TVL:
          return tvlQuery;
      }
    })();
    return {
      timePeriod,
      setTimePeriod,
      setChartType,
      priceChartType: disableCandlestickUI ? PriceChartType.LINE : priceChartType,
      setPriceChartType,
      activeQuery,
      disableCandlestickUI
    };
  }, [chartType, priceQuery, volumeQuery, tvlQuery, timePeriod, priceChartType]);
}
export default function ChartSection({ symbol, fdv, vol }) {
  const { activeQuery, timePeriod, priceChartType } = useTDPContext().chartState;
  const { tokenColor } = useTDPContext();
  const getSection = () => {
    if (activeQuery.dataQuality === DataQuality.INVALID) {
      return <ChartSkeleton
        type={activeQuery.chartType}
        height={TDP_CHART_HEIGHT_PX}
        errorText={activeQuery.loading ? void 0 : <InvalidChartMessage />}
      />;
    }
    const stale = activeQuery.dataQuality === DataQuality.STALE;
    switch (activeQuery.chartType) {
      case ChartType.PRICE:
        return <PriceChart
          symbol={symbol}
          fdv={fdv}
          vol={vol}
          data={activeQuery.entries}
          height={TDP_CHART_HEIGHT_PX}
          type={priceChartType}
          stale={stale}
        />;
      case ChartType.VOLUME:
        return <VolumeChart data={activeQuery.entries} height={TDP_CHART_HEIGHT_PX} timePeriod={timePeriod} stale={stale} />;
      case ChartType.TVL:
        return <LineChart data={activeQuery.entries} height={TDP_CHART_HEIGHT_PX} stale={stale} />;
    }
  };
  return <ThemeProvider accent1={tokenColor}><Flex
    data-cy={`tdp-${activeQuery.chartType}-chart-container`}
    testID={`tdp-${activeQuery.chartType}-chart-container`}
  >{getSection()}<ChartControls /></Flex></ThemeProvider>;
}
function ChartControls() {
  const {
    activeQuery,
    timePeriod,
    setTimePeriod,
    setChartType,
    priceChartType,
    setPriceChartType,
    disableCandlestickUI
  } = useTDPContext().chartState;
  const refitChartContent = useAtomValue(refitChartContentAtom);
  const media = useMedia();
  const isMediumScreen = media.lg;
  const showAdvancedPriceChartToggle = activeQuery.chartType === ChartType.PRICE;
  return <ChartActionsContainer><Flex
    row
    gap="$gap8"
    $md={{
      width: "100%",
      "$platform-web": {
        display: "grid",
        gridTemplateColumns: showAdvancedPriceChartToggle ? "1fr 1fr" : "1fr"
      }
    }}
  >{showAdvancedPriceChartToggle && <AdvancedPriceChartToggle
    currentChartType={priceChartType}
    onChartTypeChange={setPriceChartType}
    disableCandlestickUI={disableCandlestickUI}
  />}<ChartTypeDropdown
    options={TDP_CHART_SELECTOR_OPTIONS}
    currentChartType={activeQuery.chartType}
    onSelectOption={(c) => {
      setChartType(c);
      if (c === ChartType.PRICE) {
        setPriceChartType(PriceChartType.LINE);
      }
    }}
  /></Flex><Flex $md={{ width: "100%" }}><SegmentedControl
    fullWidth={isMediumScreen}
    options={DEFAULT_PILL_TIME_SELECTOR_OPTIONS}
    selectedOption={DISPLAYS[timePeriod]}
    onSelectOption={(option) => {
      const time = getTimePeriodFromDisplay(option);
      if (time === timePeriod) {
        refitChartContent?.();
      } else {
        setTimePeriod(time);
      }
    }}
  /></Flex></ChartActionsContainer>;
}
